import { Alert } from "@mui/material";
import * as React from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { Base } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/framework/base";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import { CompanyGroupListItem, ICompanyGroupListItem } from "../../../models/companyGroup/companyGroupListItem";
import * as companyService from "../../../models/services/companyService";
import { SelectCompanyGroupDialog } from "../../companyGroup/SelectCompanyGroupDialog/SelectCompanyGroupDialog";
import { ActionContainer } from "../../framework/ActionContainer";
import { List } from "../../framework/List/List";
import { ListIconButtonsItem } from "../../framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

export interface ICompanyEditCompanyGroupListProp {
    isReadOnly: boolean;
    companyId: string;
    companyGroups: ICompanyGroupListItem[];
    onRemoved: (ids: string[]) => void;
    onAdded: (companyGroups: ICompanyGroupListItem[]) => void;
}

interface ICompanyEditCompanyGroupListState {
    // List
    isLoading: boolean;
    companyGroups: ICompanyGroupListItem[];
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    selectedId: string;
    checkedIds: string[];

    // Dialog
    showSelectCompanyGroupDialog: boolean;
}

export class CompanyEditCompanyGroupList extends React.Component<ICompanyEditCompanyGroupListProp, ICompanyEditCompanyGroupListState> {
    constructor(props: ICompanyEditCompanyGroupListProp) {
        super(props);
        this.state = {
            isLoading: false,
            companyGroups: props.companyGroups ? props.companyGroups.slice(0) : [],
            filter: "",
            sortColumn: "name",
            sortOrderIsAsc: true,
            selectedId: null,
            checkedIds: [],
            showSelectCompanyGroupDialog: false,
        };
    }

    filteredList = (filter: string, column: string, asc: boolean) => {
        const props = this.props;
        let companyGroups = props.companyGroups ? props.companyGroups.slice(0) : [];
        if (filter) {
            const lowFilter = filter.toLowerCase();
            companyGroups = companyGroups.filter(i => i.code.toLowerCase().indexOf(lowFilter) > -1 || i.name.toLowerCase().indexOf(lowFilter) > -1 || i.comment.toLowerCase().indexOf(lowFilter) > -1);
        }
        CompanyGroupListItem.sortCompanyGroupListItems(companyGroups, column, asc);
        this.setState({
            filter,
            sortColumn: column,
            sortOrderIsAsc: asc,
            companyGroups
        });
    };

    componentDidUpdate(prevProps: ICompanyEditCompanyGroupListProp, prevState: ICompanyEditCompanyGroupListState) {
        const props = this.props;
        const state = this.state;
        if (Base.equalStrArrays(prevProps.companyGroups ? prevProps.companyGroups.map(i => i.id) : [], props.companyGroups ? props.companyGroups.map(i => i.id) : [])) return;
        this.filteredList(state.filter, state.sortColumn, state.sortOrderIsAsc);
    };

    changeFilter = (filter: string) => {
        const state = this.state;
        this.filteredList(filter, state.sortColumn, state.sortOrderIsAsc);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.filteredList(state.filter, sortColumn, sortOrderIsAsc);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    getRemoveSaveData = (props: ICompanyEditCompanyGroupListProp, companyGroupIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.companyId);
        result.append("companyGroupIds", companyGroupIds);
        return result;
    };

    handleRemove = async () => {
        const obj = this;
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (selectedIds.length < 1) return;
        const saveData = this.getRemoveSaveData(this.props, selectedIds);
        // Call server
        const result = await AppUtils.callService(() => companyService.companyEditRemoveCompanyGroups(saveData.obj));
        if (!result) return;
        obj.props.onRemoved(selectedIds);
    };
    
    handleAdd = () => {
        this.setState({
            showSelectCompanyGroupDialog: true
        });
    };

    getAddSaveData = (props: ICompanyEditCompanyGroupListProp, companyGroupIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.companyId);
        result.append("companyGroupIds", companyGroupIds);
        return result;
    };

    onSelectCompanyGroupDialogOk = async (companyGroups: ICompanyGroupListItem[]) => {
        const obj = this;
        if (!companyGroups || companyGroups.length < 1) return;
        this.setState({
            showSelectCompanyGroupDialog: false
        });
        const saveData = this.getAddSaveData(this.props, companyGroups.map(i => i.id));
        // Call server
        const result = await AppUtils.callService(() => companyService.companyEditAddCompanyGroups(saveData.obj));
        if (!result) return;
        obj.props.onAdded(companyGroups);
    };

    onSelectCompanyGroupDialogCancel = () => {
        this.setState({
            showSelectCompanyGroupDialog: false
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (<>
            <VerticalContainer
                before={<>
                    <ActionContainer
                        leftItems={
                            <ListSearchFilter
                                searchFilter={state.filter}
                                onSearchClick={this.changeFilter}
                            />
                        }
                        rightItems={
                            <ListIconButtonsItem
                                addTooltip={Translations.AddCompanyGroup}
                                removeTooltip={Translations.RemoveCompanyGroup}
                                addDisabled={props.isReadOnly}
                                removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                                onAdd={this.handleAdd}
                                onRemove={this.handleRemove}
                            />
                        }
                    />
                    {!!props.isReadOnly &&
                        <Alert severity="info">{Translations.NewCompanyMustBeSavedBeforeCompanyGroupsCanBeAdded}</Alert>
                    }
                </>}
                main={<>
                    {!props.isReadOnly &&
                        <List
                            intialized={true}
                            isLoading={state.isLoading}
                            hasMore={false}
                            height="100%"
                            globalLoadingInUse={false}
                            header={{
                                columns: [
                                    { size: 3, column: "code", content: Translations.Code },
                                    { size: 3, column: "name", content: Translations.Name },
                                    { size: 6, column: "comment", content: Translations.Description }
                                ]
                            }}
                            lines={
                                state.companyGroups.map((companyGroup) => {
                                    return {
                                        columns: [
                                            { size: 3, column: "code", content: companyGroup.code },
                                            { size: 3, column: "name", content: companyGroup.name },
                                            { size: 6, column: "comment", content: companyGroup.comment }],
                                        id: companyGroup.id
                                    };
                                })
                            }
                            noDataMessage={Translations.AddCompanyGroups}
                            sortColumn={state.sortColumn}
                            sortOrderIsAsc={state.sortOrderIsAsc}
                            selectedId={state.selectedId}
                            checkedIds={state.checkedIds}
                            onSetSelectedIds={this.handleSetSelectedIds}
                            onSearchLines={this.handleSearchLines}
                        />
                    }
                </>}
            />
            {state.showSelectCompanyGroupDialog &&
                <SelectCompanyGroupDialog
                    companyId={props.companyId}
                    multiSelect={true}
                    ignoreCompanyGroupIds={props.companyGroups.map(i => i.id)}
                    onOk={this.onSelectCompanyGroupDialogOk}
                    onCancel={this.onSelectCompanyGroupDialogCancel}
                />
            }
        </>);
    }
}